<template>
  <backbar-page-layout
    v-if="canPerform('authcore.users.get')"
    back-title="title.users"
    title="title.user_details"
    :user-id="id"
    :loading="isLoadingStatus"
  >
    <template #action>
      <b-dropdown right variant="outline-primary" text="Actions">
        <b-dropdown-item-button
          @click="isAssignRoleModalEnabled = !isAssignRoleModalEnabled"
        >
          <span class="text-muted">{{ $t('actions.assign_role') }}</span>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          @click="isChangePasswordModalEnabled = !isChangePasswordModalEnabled"
        >
          <span class="text-muted">{{ $t('actions.change_password') }}</span>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          v-b-tooltip.hover
          :title="$t('description.work_in_progress')"
        >
          <span class="text-muted">{{ $t('actions.disable_mfa') }}</span>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          @click="isLockUserModalEnabled = !isLockUserModalEnabled"
        >
          <div v-if="user.locked" class="text-muted">
            {{ $t('actions.unlock_user') }}
          </div>
          <div v-else class="text-muted">
            {{ $t('actions.lock_user') }}
          </div>
        </b-dropdown-item-button>
        <b-dropdown-item-button
          v-b-tooltip.hover
          :title="$t('description.work_in_progress')"
        >
          <span class="text-muted">{{ $t('actions.delete_user') }}</span>
        </b-dropdown-item-button>
      </b-dropdown>
    </template>
    <div>
      <b-bsq-modal
        ref="modal"
        v-model="isModalEnabled"
        :header-title="modalHeaderTitle"
        :button-title="modalButtonTitle"
        :button-variant="modalButtonVariant"
        @ok="modalSubmit"
      >
        <div class="mt-3 mb-5">
          <div v-if="isAssignRoleModalEnabled">
            <i18n path="description.new_role_assign" tag="div" class="mb-3">
              <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
            </i18n>
            <!-- TODO: Change to use b-bsq-input when it implements the multiselect feature -->
            <!-- https://gitlab.com/blocksq/bootstrap-vue/issues/28 -->
            <multiselect :value="selectedRoles" track="name" label="name" :options="roles" :multiple="true" @select="select" @remove="remove">
              <!-- <span>{{ option.name }}</span> -->
            </multiselect>
          </div>
          <div v-else-if="isLockUserModalEnabled">
            <div v-if="user.locked">
              <i18n path="description.unlock_user" tag="div" class="mb-3">
                <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
              </i18n>
            </div>
            <div v-else>
              <i18n path="description.lock_user" tag="div" class="mb-3">
                <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
              </i18n>
              <b-bsq-input
                required
                readonly
                v-model="lockContent"
                :label="$t('actions.lock_user')"
              />
            </div>
          </div>
          <div v-else-if="isChangePasswordModalEnabled">
            <i18n path="description.change_password" tag="div" class="mb-3">
              <span class="font-weight-bold" place="username">{{ user.displayName }}</span>
            </i18n>
            <b-bsq-input
              v-model="newPassword"
              :disabled="actionLoading"
              :label="$t('input.password')"
              type="password"
              autocomplete="new-password"
              class="my-3" />
            <b-bsq-input
              v-model="confirmNewPassword"
              :disabled="actionLoading"
              :label="$t('input.confirm_password')"
              type="password"
              autocomplete="new-password"
              class="my-3" />
          </div>
        </div>
      </b-bsq-modal>
      <b-container class="my-3">
        <b-row>
          <b-col class="monogram-alignment">
            <b-badge
              v-for="item in roleAssignments"
              :key="item.id"
              variant="light"
              class="badge-custom"
            >
              {{ item.name }}
            </b-badge>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col>
            <info-card id="profile" :title="$t('user_details.sub_title.profile')" :to="{ name: 'UserDetailsProfile' }">
              <div class="font-weight-bold">
                {{ $t('general.username') }}
              </div>
              <div>
                {{ user.username }}
              </div>
              <hr class="mt-1 mb-2 invisible"/>
              <div class="font-weight-bold">
                {{ $t('general.name') }}
              </div>
              <div>
                {{ user.displayName }}
              </div>
            </info-card>
          </b-col>
          <b-col>
            <info-card id="social_login" :title="$t('user_details.sub_title.social_login')" :to="{ name: 'UserDetailsSocialLogin' }">
              <div v-if="oAuthFactors && oAuthFactors.length">
                <div v-for="item in oAuthFactors" :key="item.id">
                  <div class="font-weight-bold">{{ item.service }}</div>
                  <div class="text-grey">{{ $t('user_details.text.last_used', { date: item.lastUsedAt }) }}</div>
                </div>
              </div>
              <div v-else>
                <span class="text-grey">{{ $t('user_details.text.no_social_login') }}</span>
              </div>
            </info-card>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col>
            <info-card id="email" :title="$t('user_details.sub_title.email')" :to="{ name: 'UserDetailsContact', params: { type: 'email' } }">
              <div v-if="email.length">
                <b-row v-for="item in email" :key="item.id">
                  <b-col>{{ item.value }}</b-col>
                  <b-col v-if="!item.isVerified" class="text-danger text-right">{{ $t('verification.non_verified') }}</b-col>
                </b-row>
              </div>
              <b-row v-else class="text-grey">
                <b-col>{{ $t('description.no_contact', { contact: $t('general.email') }) }}</b-col>
              </b-row>
            </info-card>
          </b-col>
          <b-col>
            <info-card id="phone" :title="$t('user_details.sub_title.phone')" :to="{ name: 'UserDetailsContact', params: { type: 'phone' } }">
              <div v-if="phone.length">
                <b-row v-for="item in phone" :key="item.id">
                  <b-col>{{ item.value }}</b-col>
                  <b-col v-if="!item.isVerified" class="text-danger text-right">{{ $t('verification.non_verified') }}</b-col>
                </b-row>
              </div>
              <b-row v-else class="text-grey">
                <b-col>{{ $t('description.no_contact', { contact: $t('general.phone') }) }}</b-col>
              </b-row>
            </info-card>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col>
            <info-card id="logs" :title="$t('user_details.sub_title.events')" :to="{ name: 'UserDetailsLogs' }">
              <span class="text-grey">{{ $t('user_details.text.account_created', { date: user.createdAt }) }}</span>
            </info-card>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col>
            <info-card id="security" :title="$t('user_details.sub_title.security')" :to="{ name: 'UserDetailsSecurity' }">
              <div class="font-weight-bold">
                {{ $t('user_details.text.password') }}
              </div>
              <!-- TODO: Last changed requires server side support, see #346 -->
              <div v-if="false" class="text-grey">
                {{ $t('user_details.text.last_changed', { date: '' } ) }}
              </div>
              <hr class="mt-1 mb-2 invisible"/>
              <b-row align-v="center">
                <b-col>
                  <div class="font-weight-bold">
                    {{ $t('user_details.text.2_step_verification') }}
                  </div>
                  <div :class="{ 'text-success': isMFAEnabled, 'text-danger': !isMFAEnabled }">
                    {{ $t(mfaStatus) }}
                  </div>
                </b-col>
                <b-col class="text-right">
                  <i v-if="isMFAEnabled" class="mx-1 text-success ac-icon ac-shield-tick"></i>
                  <i v-else class="mx-1 text-danger ac-icon ac-minus-circle"></i>
                </b-col>
              </b-row>
            </info-card>
          </b-col>
          <b-col>
            <info-card id="devices" :title="$t('user_details.sub_title.devices')" :to="{ name: 'UserDetailsDevices' }">
              <!-- TODO: Set latest session -->
              <b-row>
                <b-col>
                  <div>
                    iPhone X iOS 12.1.4
                  </div>
                  <div>
                    Chrome
                  </div>
                </b-col>
                <b-col></b-col>
              </b-row>
              <div class="text-grey">
                <i18n path="description.last_active" tag="span">
                  <span place="date">20 Jan 2018, 10:30pm</span>
                </i18n>
              </div>
            </info-card>
          </b-col>
        </b-row>
        <b-row class="my-4">
          <b-col>
            <info-card id="metadata" :title="$t('user_details.sub_title.metadata')" :to="{ name: 'UserDetailsMetadata' }">
              <b-row>
                <b-col>
                  <div class="font-weight-bold">
                    {{ $t('user_details.text.app_metadata') }}
                  </div>
                  <div class="shallow-transparency">
                    <!-- TODO: Last updated app metadata -->
                  </div>
                </b-col>
                <b-col>
                  <div class="font-weight-bold">
                    {{ $t('user_details.text.user_metadata') }}
                  </div>
                  <div class="shallow-transparency">
                    <!-- TODO: Last updated user metadata -->
                  </div>
                </b-col>
                <b-col>
                  <div class="font-weight-bold">
                    {{ $t('user_details.text.system_metadata') }}
                  </div>
                  <div class="shallow-transparency">
                    <!-- TODO: Last updated system metadata -->
                  </div>
                </b-col>
              </b-row>
            </info-card>
          </b-col>
        </b-row>
      </b-container>
    </div>
  </backbar-page-layout>
</template>

<style src="vue-multiselect/dist/vue-multiselect.min.css">
</style>

<script>
import { mapState } from 'vuex'
import Multiselect from 'vue-multiselect'

import BackbarPageLayout from '@/components/BackbarPageLayout.vue'
import InfoCard from '@/components/InfoCard.vue'

import store from '@/store'
import i18n from '@/i18n'

export default {
  name: 'UserDetails',
  components: {
    BackbarPageLayout,
    InfoCard,

    Multiselect
  },

  props: {
    id: {
      type: Number,
      required: true
    }
  },

  data () {
    return {
      isProcessing: false,
      isAssignRoleModalEnabled: false,
      isChangePasswordModalEnabled: false,
      isDisableMFAModalEnabled: false,
      isLockUserModalEnabled: false,
      isDeleteUserModalEnabled: false,

      // Roles assigned for the user
      selectedRoles: [],
      // Roles to be unassigned from the user
      removedRoles: [],
      lockContent: i18n.t('general.permanently'),

      // Change password
      newPassword: '',
      confirmNewPassword: ''
    }
  },

  computed: {
    ...mapState('client', [
      'authenticated'
    ]),
    ...mapState('management/userDetails/security', [
      'mfaDevices'
    ]),
    ...mapState('management/rolesList', [
      'roles'
    ]),
    ...mapState('management/userDetails', [
      'user',
      'roleAssignments',
      'loading',
      'error',

      'actionLoading',
      'actionError'
    ]),
    ...mapState('management/userDetails/oauthFactorsList', [
      'oAuthFactors'
    ]),
    ...mapState('management/userDetails/contactsList', {
      email: state => state.contacts.filter(contact => contact.type === 'email'),
      phone: state => state.contacts.filter(contact => contact.type === 'phone')
    }),
    isLoadingStatus () {
      return !this.authenticated || this.loading
    },
    isMFAEnabled () {
      return this.mfaDevices.length > 0
    },
    mfaStatus () {
      return this.isMFAEnabled ? 'general.on' : 'general.off'
    },
    isModalEnabled: {
      get () {
        return this.isAssignRoleModalEnabled ||
               this.isChangePasswordModalEnabled ||
               this.isDisableMFAModalEnabled ||
               this.isLockUserModalEnabled ||
               this.isDeleteUserModalEnabled
      },
      // isModalEnabled will only be set when modal is closed, therefore it sets all modal related
      // value to false. Case for opening modal is handled by individual modal value.
      //
      // Setting timeout as to keep the content in modal when it is fading out.
      set () {
        setTimeout(() => {
          this.isAssignRoleModalEnabled = false
          this.isChangePasswordModalEnabled = false
          this.isDisableMFAModalEnabled = false
          this.isLockUserModalEnabled = false
          this.isDeleteUserModalEnabled = false
        }, 500)
      }
    },
    modalHeaderTitle () {
      if (this.isAssignRoleModalEnabled) {
        return i18n.t('actions.assign_role')
      } else if (this.isLockUserModalEnabled) {
        if (this.user.locked) {
          return i18n.t('actions.unlock_user')
        } else {
          return i18n.t('actions.lock_user')
        }
      } else if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonTitle () {
      if (this.isAssignRoleModalEnabled) {
        return i18n.t('actions.assign_role')
      } else if (this.isLockUserModalEnabled) {
        if (this.user.locked) {
          return i18n.t('actions.unlock_user')
        } else {
          return i18n.t('actions.lock_user')
        }
      } else if (this.isChangePasswordModalEnabled) {
        return i18n.t('actions.change_password')
      }
      return ''
    },
    modalButtonVariant () {
      if (this.isAssignRoleModalEnabled || this.isLockUserModalEnabled || this.isChangePasswordModalEnabled) {
        return 'primary'
      }
      return ''
    }
  },

  watch: {
    async authenticated (newVal) {
      if (newVal) {
        await store.dispatch('management/userDetails/get', this.id)
      } else {
        // Clear the error message from unauthenticated state
        store.commit('management/userDetails/CLEAR_STATES')
      }
    },

    roleAssignments (newVal) {
      this.selectedRoles = []
      this.removedRoles = []
      // As the object from two API are different by reference, filter the item by id and
      // push the item into the selectedRoles array since it requires reference match with
      // roles object, which supplies the multiselect items.
      newVal.forEach(assignmentItem => {
        const roleItem = this.roles.find(item => item.id === assignmentItem.id)
        if (roleItem !== undefined) {
          roleItem.originalAssigned = true
          this.selectedRoles.push(roleItem)
        }
      })
    },

    async user (newVal) {
      if (newVal !== undefined) {
        await Promise.all([
          store.dispatch('management/userDetails/listRole', this.user.id),
          store.dispatch('management/userDetails/contactsList/list', {}),
          store.dispatch('management/rolesList/list'),
          store.dispatch('management/userDetails/oauthFactorsList/list', this.user.id)
        ])
      }
    }
  },

  async mounted () {
    await Promise.all([
      store.dispatch('management/userDetails/get', this.id),
      store.dispatch('management/userDetails/security/list', this.id)
    ])
  },

  destroyed () {
    this.selectedRoles = []
    this.removedRoles = []

    store.commit('management/userDetails/contactsList/CLEAR_STATES')
  },

  methods: {
    // Select to add new item in assigning roles. If the item added is from removedRoles, add
    // that item instead. The flag originalAssigned will prevent the item to be added in submission.
    select (selectedItem) {
      const itemInRemovedRoles = this.removedRoles.find((item) => item.id === selectedItem.id)
      if (itemInRemovedRoles !== undefined) {
        if (itemInRemovedRoles.originalAssigned) {
          this.removedRoles = this.removedRoles.filter(item => item.id !== selectedItem.id)
          this.selectedRoles.push(itemInRemovedRoles)
        }
      } else {
        this.selectedRoles.push(selectedItem)
      }
    },
    // Remove item in assigning roles. If the item is assigned originally it should
    // push to removedRoles for role unassignment usage in submission.
    remove (removedItem) {
      const itemInSelectedRoles = this.selectedRoles.find((item) => item.id === removedItem.id)
      if (itemInSelectedRoles !== undefined) {
        if (itemInSelectedRoles.originalAssigned) {
          this.removedRoles.push(removedItem)
        }
      }
      this.selectedRoles = this.selectedRoles.filter(item => item.id !== removedItem.id)
    },
    async modalSubmit () {
      if (this.isAssignRoleModalEnabled) {
        await this.assignRoles()
      } else if (this.isLockUserModalEnabled) {
        if (this.user.locked) {
          await this.unlock()
        } else {
          await this.lock()
        }
      } else if (this.isChangePasswordModalEnabled) {
        await this.changePassword()
      }

      if (this.actionError === undefined) {
        this.$nextTick(() => {
          this.isAssignRoleModalEnabled = false
          this.isChangePasswordModalEnabled = false
          this.isDisableMFAModalEnabled = false
          this.isLockUserModalEnabled = false
          this.isDeleteUserModalEnabled = false
        })
      }
    },
    async assignRoles () {
      await this.selectedRoles.filter(item => item.originalAssigned === undefined).forEach(item => {
        store.dispatch('management/userDetails/assign', item.id)
      })
      await this.removedRoles.forEach(item => {
        store.dispatch('management/userDetails/unassign', item.id)
      })
      // Reset the origianlAssigned state for the assignment after submission as the role list will
      // only refresh in mounted status
      this.roles.filter(item => item.originalAssigned).forEach(item => { item.originalAssigned = undefined })
    },
    // For current design, support infinity lock with no description
    async lock () {
      await store.dispatch('management/userDetails/lock', {
        isLocked: true,
        lockInDays: Infinity,
        description: ''
      })
    },
    async unlock () {
      await store.dispatch('management/userDetails/lock', {
        isLocked: false,
        lockInDays: Infinity,
        description: ''
      })
    },
    async changePassword () {
      await store.dispatch('management/userDetails/changePassword', {
        newPassword: this.newPassword,
        confirmNewPassword: this.confirmNewPassword
      })
    }
  }
}
</script>

<style lang="scss">
.ac-shield-tick,
.ac-minus-circle {
    font-size: 1.25rem;
}
</style>
